import { http, type HttpTransportConfig, type Transport } from 'viem'

import type { ChainId } from '../web3.types'

import { ANVIL_MAX_CALLDATA_BYTES } from './anvil'
import { getAlchemyAuthHeaders, getAlchemyMaxBlockRange, getAlchemyRpcUrl } from './chains/alchemy'
import { getEthCallGasCapForEndpoint } from './chains/gas-limits'

/** @internal */
export const ERPC_BASE_URL = 'https://rpc.morpho.dev'

export type ErpcEndpoint = 'cache' | 'realtime' | 'experimental'

type AlchemyArgs = { apiKey: string | undefined }

/** @internal */
export type CustomArgs = { url: string | undefined }

type ErpcArgs = {
  secret: string | undefined
  endpoint: ErpcEndpoint
  /** Only honored when `endpoint === 'cache'`. Seconds. Sent as `X-ERPC-Cache-Max-Age`. */
  maxAge?: number
}

/**
 * The output of `rpcs.<source>.chain(chainId).with(args, opts)` — one entry in
 * an RPC failover stack. Carries the constructed URL, an http transport (when
 * the required args are present), and the recommended limits/timeouts for the
 * (chain, source) pair. Use `hasTransport` to narrow before composing failover.
 */
export type RpcCandidate = {
  /** `undefined` when required args (apiKey / secret) are missing or chain is unsupported. */
  transport: Transport | undefined
  /**
   * Constructed URL (also `undefined` when args missing or chain unsupported). Never
   * carries credentials for alchemy — the API key rides in an `Authorization` header
   * on the transport, so `vercel.serverless-runtime` spans naming `POST <host>/<path>`
   * cannot leak it.
   */
  url: string | undefined
  /** Recommended viem block-range cap for `eth_getLogs`-style queries. */
  maxBlockRange: number
  /** Recommended `eth_call` gas cap for this (chain, source) pair. */
  gasLimit: number | undefined
  /**
   * Largest `eth_call` calldata this source accepts, when it is small enough to matter. `undefined`
   * on every real provider: gas caps a deployless chunk long before its bytes do, so stating a
   * budget nothing reaches would only read as a considered bound. Anvil is the exception.
   */
  batchSize: number | undefined
  /** Recommended fetch timeout in ms. Caller can override via `opts`. */
  timeout: number
}

type ChainBuilder<TArgs> = {
  with: (args: TArgs, opts?: HttpTransportConfig) => RpcCandidate
}

/**
 * Configurable factory for building RPC candidates against a specific service
 * (alchemy / erpc) or any caller-supplied URL (custom). Apply per-chain with
 * `.chain(chainId)` then per-config with `.with(args, opts)`.
 * @internal
 */
export type RpcSource<TArgs> = {
  chain: (chainId: ChainId) => ChainBuilder<TArgs>
}

const alchemySource: RpcSource<AlchemyArgs> = {
  chain: chainId => ({
    with: ({ apiKey }, opts) => {
      const url = apiKey ? getAlchemyRpcUrl(chainId) : undefined
      const timeout = 3_000
      const fetchOptions = apiKey
        ? {
            ...opts?.fetchOptions,
            headers: { ...opts?.fetchOptions?.headers, ...getAlchemyAuthHeaders(apiKey) }
          }
        : opts?.fetchOptions
      return {
        url,
        transport: url ? http(url, { timeout, ...opts, fetchOptions }) : undefined,
        maxBlockRange: getAlchemyMaxBlockRange(chainId) ?? 10_000,
        gasLimit: url ? getEthCallGasCapForEndpoint(chainId, url) : undefined,
        batchSize: undefined,
        timeout
      }
    }
  })
}

const erpcSource: RpcSource<ErpcArgs> = {
  chain: chainId => ({
    with: ({ secret, endpoint, maxAge }, opts) => {
      const timeout = 30_000
      if (!secret) {
        return {
          url: undefined,
          transport: undefined,
          maxBlockRange: 10_000,
          gasLimit: undefined,
          batchSize: undefined,
          timeout
        }
      }
      const url = `${ERPC_BASE_URL}/${endpoint}/evm/${chainId}?secret=${secret}`
      const cacheHeader =
        endpoint === 'cache' && maxAge !== undefined
          ? { 'X-ERPC-Cache-Max-Age': String(maxAge) }
          : undefined
      const fetchOptions = cacheHeader
        ? {
            ...opts?.fetchOptions,
            headers: { ...opts?.fetchOptions?.headers, ...cacheHeader }
          }
        : opts?.fetchOptions
      return {
        url,
        transport: http(url, { timeout, ...opts, fetchOptions }),
        maxBlockRange: endpoint === 'realtime' ? 10_000 : 150_000,
        gasLimit: getEthCallGasCapForEndpoint(chainId, url),
        batchSize: undefined,
        timeout
      }
    }
  })
}

/**
 * Caller-driven URL source — wraps any URL the caller supplies (public RPCs, custom upstreams) in
 * an http transport with sensible defaults. Anvil forks have {@link rpcs.anvil}, which states a
 * fork's request budget.
 * Always emits a transport when `url` is provided; emits `undefined` otherwise
 * so callers can `.filter(hasTransport)` to drop unconfigured candidates.
 */
const customSource: RpcSource<CustomArgs> = {
  chain: () => ({
    with: ({ url }, opts) => {
      const timeout = 30_000
      return {
        url,
        transport: url ? http(url, { timeout, ...opts }) : undefined,
        maxBlockRange: 10_000,
        gasLimit: undefined,
        batchSize: undefined,
        timeout
      }
    }
  })
}

/**
 * An anvil fork. Identical to {@link customSource} but for the request budget a deployless chunk
 * keeps against a fork; see {@link ANVIL_MAX_CALLDATA_BYTES}.
 */
const anvilSource: RpcSource<CustomArgs> = {
  chain: chainId => ({
    with: (args, opts) => ({
      ...customSource.chain(chainId).with(args, opts),
      batchSize: ANVIL_MAX_CALLDATA_BYTES
    })
  })
}

export const rpcs = {
  alchemy: alchemySource,
  anvil: anvilSource,
  erpc: erpcSource,
  custom: customSource
} as const

/**
 * Type guard narrowing an `RpcCandidate` to one with a non-`undefined` transport.
 * Use in `.filter(hasTransport)` to drop candidates whose env vars are unset
 * before composing failover branches.
 */
export function hasTransport(
  candidate: RpcCandidate
): candidate is RpcCandidate & { transport: NonNullable<RpcCandidate['transport']> } {
  return candidate.transport !== undefined
}

/**
 * Workaround for `viem-dlc`'s `failover` erasing homogeneous branch types to
 * `Transport<'viem-dlc-failover'>`. Also rewrites the runtime `config.type`/`key`/`name`, so
 * telemetry keyed on transport type sees the branch type, not the failover. Delete this and
 * both named wrappers once `failover` preserves a homogeneous branch type upstream.
 */
function asTransportType<type extends string>(
  transport: Transport<string, unknown>,
  type: type,
  name: string
): Transport<type> {
  return (opts => {
    const t = transport(opts)
    return {
      ...t,
      config: {
        ...t.config,
        type,
        key: type,
        name
      }
    }
  }) as Transport<type>
}

/**
 * `viem-dlc`'s `failover` returns `Transport<'viem-dlc-failover'>`, but when every
 * branch underneath is a `cache(...)` transport the underlying RPC schema is
 * identical. Override the transport `type`/`key`/`name` fields so downstream
 * code that's typed against `Transport<'viem-dlc-cache'>` keeps compiling.
 */
export function asCacheTransport(
  transport: Transport<string, unknown>
): Transport<'viem-dlc-cache'> {
  return asTransportType(transport, 'viem-dlc-cache', '[viem-dlc] cache')
}

/**
 * {@link asCacheTransport}'s counterpart for `deployless(...)` branches. Drops the
 * deployless `value` typing (`{ gasLimit }`) — no consumer reads it through the client.
 */
export function asDeploylessTransport(
  transport: Transport<string, unknown>
): Transport<'viem-dlc-deployless'> {
  return asTransportType(transport, 'viem-dlc-deployless', '[viem-dlc] deployless')
}
